<template>
  <div
    :class="[
      'content-row__main-info',
      isLoading && 'content-row__main-info--disabled',
    ]"
  >
    <div class="content-row__main-info__data">
      <div class="content-row__main-info__data__title">
        <div>{{ title }}</div>
        <LockIcon
          v-if="locked"
          v-tooltip="{
            content: getAccessTooltip,
            placement: 'right-center',
          }"
          class="content-row__main-info__data__title--icon"
        />
        <RunningPill v-if="isLabRunning" :showLabel="false" />
      </div>
      <div class="content-row__main-info__data__bottom">
        <div
          v-if="content.learning_areas && content.learning_areas.length"
          class="content-row__main-info__data__bottom__las-wrapper"
        >
          <LearningAreaPointer
            v-for="learningArea in content.learning_areas"
            :key="learningArea.name"
            :name="learningArea.name"
            :color="getLearningAreaColorByName(learningArea.name)"
          />
        </div>
        <span v-if="difficulty">
          {{ difficulty }}
        </span>
        <span v-if="duration">
          {{ duration }}
        </span>
        <span
          v-if="completed"
          class="content-row__main-info__data__bottom--completed"
        >
          <GreencheckIcon /> Completed</span
        >
      </div>
    </div>
    <div class="content-row__main-info__info-btn-wrapper">
      <button
        v-tooltip="{
          content: 'More information',
          placement: 'top-center',
        }"
        @click.stop="toggleExpand"
      >
        <RotatingContainer :play="isLoading">
          <LoaderIcon v-if="isLoading" />
          <InfoIcon v-else />
        </RotatingContainer>
      </button>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { RunningPill, LearningAreaPointer } from '@/components/properties'
import { getLearningAreaColorByName, getLearningAreaName } from '@/helpers'
import RotatingContainer from '@/components/shared/RotatingContainer.vue'
import { InfoIcon, LoaderIcon, LockIcon } from 'vue-feather-icons'
import GreencheckIcon from '@/assets/icons/greencheck.svg'

export default {
  name: 'ContentRowMainInfo',
  components: {
    RunningPill,
    LearningAreaPointer,
    RotatingContainer,
    LoaderIcon,
    InfoIcon,
    GreencheckIcon,
    LockIcon,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      getLearningAreaColorByName,
    }
  },
  computed: {
    ...mapState({
      runningLabSession: state => state.labs.runningLabSession,
    }),
    ...mapGetters({
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isAccOwnerOrTeamManager: 'auth/isAccOwnerOrTeamManager',
      isBusinessUser: 'business/isBusinessUser',
    }),
    contentType() {
      return this.content.content_type
    },
    isLabRunning() {
      const { content, runningLabSession } = this
      const id = content && content.uuid ? content.uuid : content.objectID
      return id === runningLabSession?.lab_id
    },
    title() {
      return this.content.name
    },
    difficulty() {
      return this.content.difficulty ?? ''
    },
    duration() {
      return this.$duration.durationFromSecs(this.content.duration, 'minutes')
    },
    completed() {
      return this.content.user_status === 'finished'
    },
    publishedDate() {
      return new Date(this.content.published_date).getTime()
    },
    getAccessTooltip() {
      if (this.locked) {
        if (this.isSkillDiveB2CUser) {
          return `This lab is part of the ${getLearningAreaName(
            this.content
          )} learning area. Get access.`
        } else if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
          return 'This lab is locked. Request access.'
        } else {
          return 'This lab is locked. Get access.'
        }
      }
      return ''
    },
  },
  methods: {
    toggleExpand() {
      this.$emit('toggle-expand')
    },
  },
}
</script>
<style scoped lang="scss">
.content-row {
  &__main-info {
    font-family: var(--font-book);

    display: flex;
    gap: 0 5px;
    flex: 1 0 auto;
    height: 65px;

    &__data {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: center;
      align-items: flex-start;
      gap: 5px;

      @include mobile {
        gap: 10px;
      }

      &__title {
        display: flex;
        overflow: hidden;
        align-items: center;

        height: 28px;
        font-family: var(--font-bold);
        font-style: normal;
        font-weight: 700;
        font-size: rem(12px);
        line-height: rem(14px);

        @include mobile {
          height: 24px;
          font-family: var(--font-medium);
          font-weight: 500;
          font-size: rem(18px);
          line-height: rem(24px);
        }

        .running-pill {
          background: transparent;
        }

        &--icon {
          width: 40px;
          height: 40px;
          padding: 12px;
        }
      }

      &__bottom {
        display: flex;
        align-items: center;
        column-gap: 10px;
        font-family: var(--font-book);
        height: 14px;
        font-style: normal;
        font-weight: 450;
        font-size: rem(10px);
        line-height: rem(12px);
        margin-top: 5px;
        grid-column: 1;
        grid-row: 3;
        overflow: hidden;

        @include mobile {
          height: 14px;
          margin: 0;
          font-size: rem(12px);
          line-height: rem(14px);
        }

        &__las-wrapper {
          display: flex;
          gap: 10px;
        }

        * {
          white-space: nowrap;
          text-transform: capitalize;
          border-right: 1px solid var(--perfect-gray);
          padding-right: 10px;
          height: 12px;

          &:last-child {
            border-right: none;
          }

          @include mobile {
            height: 14px;
          }
        }

        &--completed {
          color: var(--positive-light);
          display: flex;
          align-items: center;
          gap: 5px;
          svg {
            width: 14px;
            padding: 0;
          }
        }
      }
    }

    &__info-btn-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;

      @include mobile {
        grid-column: 2;
        grid-row: 1 / 4;
      }

      button {
        display: flex;
        justify-content: center;
        align-items: center;
        background: transparent;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        cursor: pointer;

        &:hover {
          background: var(--white-10);
        }

        svg {
          color: var(--white);
          width: 16px;
          height: 16px;
        }
      }
    }

    &--disabled {
      cursor: not-allowed;
    }
  }
}
</style>
